import { createSelector } from 'reselect';
import omit from 'lodash/omit';
import { orderBy } from 'natural-orderby';
import {
  getAssetManagerFilters,
  getAssetManagerSelectedAssetTypeId,
  getAssetManagerShowPlanView,
  getAssetStatusesKeyedById,
  getAssetTypes,
  getCurrentPlanBandsForCharts,
  getPlannedTwinIDsKeyedByTwinId,
  getSelectedAssetType,
  getSummariesConfig,
} from '..';
import { getFilteredAssets, getFilteredAssetsContext, getAssetsKeyedByBandId } from '.';
import { keyAssetsByStatus } from '../../utils';
import map from '../map';
import { FieldSummaryConfig } from '../../config/types';
import { DEFAULT_SUMMARIES } from '../../config/constants';

export const getFieldSummariesConfig = createSelector(
  [
    getSummariesConfig,
    getAssetManagerSelectedAssetTypeId,
  ],
  (
    summariesConfig,
    assetManagerSelectedAssetTypeId,
  ) => {
    if (!summariesConfig) return undefined;
    const allSummaries = summariesConfig[assetManagerSelectedAssetTypeId] || DEFAULT_SUMMARIES;
    return allSummaries.filter(
      summary => summary.type === 'field_summary',
    ) as FieldSummaryConfig[];
  },
);

const getFilteredAssetsForFieldSummaryKeyedByField = createSelector(
  [
    getAssetManagerFilters,
    getFilteredAssetsContext,
    getAssetManagerSelectedAssetTypeId,
    getFieldSummariesConfig,
  ],
  (
    assetManagerFilters,
    filteredAssetsContext,
    selectedAssetTypeId,
    fieldSummariesConfig,
  ) => {
    const {
      searchTags,
      searchText,
      dateFilter,
      assetStatusFilter,
      observationTypeFilter,
      tableFilter,
      openedLayerId,
      selectedBandIds,
      multiSearchModeFilter,
      selectedLayerIds,
      fieldFilters,
    } = assetManagerFilters;
    return new Map(fieldSummariesConfig?.map((({ field }) => {
      const customFieldFilters = omit(fieldFilters, field);

      const filteredAssets = getFilteredAssets(filteredAssetsContext, {
        searchTags,
        searchText,
        dateFilter,
        selectedAssetTypeId,
        selectedLayerIds,
        assetStatusFilter,
        selectedBandIds,
        observationTypeFilter,
        tableFilter,
        openedLayerId,
        multiSearchModeFilter,
        fieldFilters: customFieldFilters,
      });
      return [field, filteredAssets];
    })));
  },
);

export const getAssetsKeyedByStatusForFieldSummaries = createSelector(
  [getAssetTypes, getFilteredAssetsForFieldSummaryKeyedByField],
  (assetTypes, filteredAssetsForFieldSummaryKeyedByField) => new Map(
    [...filteredAssetsForFieldSummaryKeyedByField].map(([key, value]) => [
      key,
      keyAssetsByStatus(assetTypes, value),
    ]),
  ),
);

export const getFilteredAssetsKeyedByBandIdForFieldSummaries = createSelector(
  [getPlannedTwinIDsKeyedByTwinId, getFilteredAssetsForFieldSummaryKeyedByField],
  (plannedTwinIdsKeyedByTwinId, filteredAssetsForFieldSummaryKeyedByField) => new Map(
    [...filteredAssetsForFieldSummaryKeyedByField].map(([key, value]) => [
      key,
      getAssetsKeyedByBandId(plannedTwinIdsKeyedByTwinId, value),
    ]),
  ),
);

export const getFieldSummaryOptionsKeyedByField = createSelector(
  [
    map.presenterModeActive,
    getAssetManagerFilters,
    getFilteredAssetsForFieldSummaryKeyedByField,
    getSelectedAssetType,
    getFieldSummariesConfig,
  ],
  (
    presenterModeActive,
    assetManagerFilters,
    assetsArrayKeyedByField,
    selectedAssetType,
    fieldSummariesConfig,
  ): Map<string, Array<{ label: string, value: string }>> => {
    if (!fieldSummariesConfig) return new Map();
    return new Map(fieldSummariesConfig?.map(({ field, generateOptionsFromAssetList }) => {
      const fields = selectedAssetType?.fields || [];
      const { fieldFilters } = assetManagerFilters;
      const fieldFilter = fieldFilters?.[field];
      let fieldOptions: Array<{
        label: string,
        value: string,
      }> = [];
      const assetsArray = assetsArrayKeyedByField.get(field) || [];
      const fieldListFromAsset = new Map(
        assetsArray
          ?.filter(asset => asset.type === selectedAssetType.type)
          ?.map(asset => [`${asset.fields?.[field] || ''}`, asset]),
      );

      const fieldConfig = fields?.find(fieldObj => fieldObj.id === field);
      if (generateOptionsFromAssetList) {
        fieldOptions = [...fieldListFromAsset.keys()]
          .filter(value => !!value)
          .sort()
          .map(value => ({ label: value, value }));
        fieldOptions = orderBy(fieldOptions, [i => i.label]);
      } else if (fieldConfig) {
        fieldOptions = fieldConfig?.properties?.options ?? [];
      }

      if (fieldListFromAsset.get('') && !fieldOptions.some(o => o.value === '')) {
        fieldOptions = [...fieldOptions, { label: '(blank)', value: '' }];
      }

      if (presenterModeActive && fieldFilter?.length) {
        fieldOptions = fieldOptions.filter(
          ({ value }) => fieldFilter.includes(value),
        );
      }

      return [field, fieldOptions];
    }));
  },
);

export const getChartistDataKeyedByFieldSummary = createSelector(
  [
    getAssetManagerShowPlanView,
    getAssetStatusesKeyedById,
    getAssetsKeyedByStatusForFieldSummaries,
    getSelectedAssetType,
    getFieldSummaryOptionsKeyedByField,
    getCurrentPlanBandsForCharts,
    getFilteredAssetsKeyedByBandIdForFieldSummaries,
    getFieldSummariesConfig,
  ],
  (
    showPlanView,
    assetStatusesKeyedById,
    assetsKeyedByStatusForFieldSummaries,
    selectedAssetType,
    fieldSummaryOptionsKeyedByField,
    currentPlanBands,
    assetsKeyedByBandIdForFieldSummaries,
    fieldSummariesConfig,
  ) => {
    const { statuses } = selectedAssetType;
    if (!fieldSummariesConfig) return new Map();

    return new Map(fieldSummariesConfig?.map(({ field }) => {
      const options = fieldSummaryOptionsKeyedByField.get(field)! ?? [];
      const getStatus = () => {
        const series = statuses?.map((status) => {
          const statusId = status.id;
          const assets = assetsKeyedByStatusForFieldSummaries.get(field)?.get(statusId)!;
          const statusLabel = assetStatusesKeyedById.get(statusId)?.label;
          let newSubSeries: Array<{ meta: string, value: number }> = [];

          newSubSeries = options.map(({ value }) => {
            const count = assets?.filter(({ fields }) => {
              const assetVal = fields?.[field];
              if (assetVal == null || assetVal === '') {
                return value === '';
              }
              return value === `${assetVal}`;
            })?.length || 0;
            return {
              meta: statusLabel!,
              value: count,
            };
          });
          return newSubSeries;
        });
        return series?.reverse() || [];
      };

      const getPlan = () => currentPlanBands.map((band) => {
        const assets = assetsKeyedByBandIdForFieldSummaries.get(field)?.get(band.id) || [];
        const newSubSeries = options?.map(({ value }) => {
          const count = assets.filter(asset => asset?.fields?.[field] === value).length;
          return {
            meta: band.label,
            value: count,
          };
        });

        return newSubSeries;
      }).reverse();
      const labels = options.map(({ label }) => label) as Array<string>;
      const data = {
        labels,
        series: showPlanView ? getPlan() : getStatus(),
      };

      return [field, data];
    }));
  },
);
